import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from 'reactstrap';

export interface AssignPanelOperator {
  user_id: number;
  label: string;
  email: string | null;
}

export interface PickListAssignPanelProps {
  /** External id of the pick-list, shown in the panel title. */
  pickListLabel: string;
  /** Voice-operators of the warehouse (from the assignment board). */
  operators: AssignPanelOperator[];
  /** Assign mutation in-flight. */
  pending?: boolean;
  /** Error surfaced from the last assign attempt. */
  error?: string | null;
  /** Confirm: assign the whole list to the selected operator. */
  onConfirm: (operatorUserId: number) => void | Promise<void>;
  /** Close the panel without assigning. */
  onClose: () => void;
}

/**
 * SP-8d — pannello inline "Assegna lista". Mostrato SOPRA la sezione clienti
 * quando la lista è `open` (prima assegnazione, via assign-whole). Gli operatori
 * sono bottoni orizzontali in linea (flex-wrap) con selezione singola; lo stato
 * attivo usa lo stile primary (verde Velzon). La riassegnazione di liste già
 * assegnate resta nella sezione Missioni (decisione SP-8d).
 */
export function PickListAssignPanel({
  pickListLabel, operators, pending, error, onConfirm, onClose,
}: PickListAssignPanelProps) {
  const { t } = useTranslation('picklist');
  const [selected, setSelected] = useState<number | null>(null);

  return (
    <div className="pl-assign-panel border rounded p-3 mb-3 bg-body">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h3 className="fs-15 fw-semibold mb-0">
          {t('assign.title', { label: pickListLabel })}
        </h3>
        <Button type="button" color="link" size="sm" className="text-muted p-0"
                onClick={onClose} aria-label={t('assign.close_aria')}>
          <i className="bi bi-x-lg" aria-hidden="true" />
        </Button>
      </div>

      {error != null && <Alert color="danger" className="py-2 px-3 mb-2">{error}</Alert>}

      {operators.length === 0 ? (
        <p className="text-muted mb-0">{t('assign.no_operators')}</p>
      ) : (
        <>
          <p className="small text-muted mb-2">{t('assign.select_operator')}</p>
          <div className="d-flex flex-wrap gap-2 mb-3">
            {operators.map((op) => {
              const isSel = selected === op.user_id;
              return (
                <Button
                  key={op.user_id}
                  type="button"
                  color={isSel ? 'primary' : 'secondary'}
                  outline={!isSel}
                  size="sm"
                  aria-pressed={isSel}
                  onClick={() => setSelected(op.user_id)}
                >
                  <i className={`bi ${isSel ? 'bi-record-circle-fill' : 'bi-circle'} align-bottom me-1`}
                     aria-hidden="true" />
                  {op.label}
                </Button>
              );
            })}
          </div>
          <div className="d-flex justify-content-start">
            <Button
              type="button"
              color="primary"
              size="sm"
              disabled={selected === null || pending === true}
              onClick={() => { if (selected !== null) void onConfirm(selected); }}
            >
              {t('assign.confirm')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
